import React from "react";
import { FaClock } from "react-icons/fa6";
import { PiBracketsCurly } from "react-icons/pi";
import { BiMessageDetail } from "react-icons/bi";

// SMS Metrics Display Component
function SmsMetricsDisplay({ description, placeholders }) {
  const calculateSmsMetrics = () => {
    if (!description) {
      return {
        currentChars: 0,
        estimatedMinChars: 0,
        estimatedMaxChars: 0,
        minSmsCount: 0,
        maxSmsCount: 0,
      };
    }

    // Current character count (with placeholders as-is)
    const currentChars = description.length;

    // Estimated character counts
    let estimatedMinChars = description;
    let estimatedMaxChars = description;

    // Common placeholder value estimates
    const placeholderEstimates = {
      min: 5,   // e.g., "John"
      max: 30,  // e.g., "123 Main Street, Apartment 4B"
    };

    // Replace each placeholder with estimated values
    if (placeholders && placeholders.length > 0) {
      placeholders.forEach((placeholder) => {
        const pattern = `{{${placeholder}}}`;
        
        // Estimate min: replace with short value
        estimatedMinChars = estimatedMinChars.replace(
          new RegExp(pattern.replace(/[{}]/g, '\\$&'), 'g'),
          'X'.repeat(placeholderEstimates.min)
        );
        
        // Estimate max: replace with long value
        estimatedMaxChars = estimatedMaxChars.replace(
          new RegExp(pattern.replace(/[{}]/g, '\\$&'), 'g'),
          'X'.repeat(placeholderEstimates.max)
        );
      });
    }

    const minChars = estimatedMinChars.length;
    const maxChars = estimatedMaxChars.length;

    // Calculate SMS count (153 characters per SMS)
    const SMS_LIMIT = 153;
    const minSmsCount = Math.ceil(minChars / SMS_LIMIT);
    const maxSmsCount = Math.ceil(maxChars / SMS_LIMIT);

    return {
      currentChars,
      estimatedMinChars: minChars,
      estimatedMaxChars: maxChars,
      minSmsCount,
      maxSmsCount,
    };
  };

  const metrics = calculateSmsMetrics();

  // Color coding based on SMS count
  const getSmsCountColor = (count) => {
    if (count === 1) return "text-green-600 bg-green-50 border-green-200";
    if (count === 2) return "text-yellow-600 bg-yellow-50 border-yellow-200";
    return "text-red-600 bg-red-50 border-red-200";
  };

  const getSmsIconColor = (count) => {
    if (count === 1) return "text-green-600";
    if (count === 2) return "text-yellow-600";
    return "text-red-600";
  };

  return (
    <div className="space-y-3">
      {/* Character Count */}
      <div className="flex items-center justify-between text-sm">
        <span className="text-darkgray">Current Characters:</span>
        <span className="font-semibold text-darkergray">
          {metrics.currentChars}
        </span>
      </div>

      {/* Estimated Character Range */}
      <div className="flex items-center justify-between text-sm">
        <span className="text-darkgray">Estimated Character Range:</span>
        <span className="font-semibold text-darkergray">
          {metrics.estimatedMinChars} - {metrics.estimatedMaxChars}
        </span>
      </div>

      {/* SMS Count Estimate */}
      <div className="pt-3 border-t border-mediumgray">
        <div className="text-sm text-darkgray mb-2 font-medium">
          SMS Count Estimate:
        </div>
        <div className="flex items-center gap-3">
          {/* Minimum SMS */}
          <div className={`flex-1 flex items-center justify-between px-3 py-2 rounded-lg border ${getSmsCountColor(metrics.minSmsCount)}`}>
            <div className="flex items-center gap-2">
              <BiMessageDetail className={getSmsIconColor(metrics.minSmsCount)} size={16} />
              <span className="text-xs font-medium">Min</span>
            </div>
            <span className="font-bold text-lg">
              {metrics.minSmsCount}
            </span>
          </div>

          {/* Arrow */}
          <div className="text-darkgray">→</div>

          {/* Maximum SMS */}
          <div className={`flex-1 flex items-center justify-between px-3 py-2 rounded-lg border ${getSmsCountColor(metrics.maxSmsCount)}`}>
            <div className="flex items-center gap-2">
              <BiMessageDetail className={getSmsIconColor(metrics.maxSmsCount)} size={16} />
              <span className="text-xs font-medium">Max</span>
            </div>
            <span className="font-bold text-lg">
              {metrics.maxSmsCount}
            </span>
          </div>
        </div>
      </div>

      {/* Info Note */}
      <div className="pt-2 text-xs text-darkgray bg-gray-50 p-2 rounded">
        <div>• Placeholder values estimated at 5-30 characters</div>
        <div>• Each SMS supports up to 153 characters</div>
      </div>
    </div>
  );
}

export default function SmsTemplateView({ template }) {
  if (!template) return null;

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString("en-AU", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  return (
    <div className="bg-white">
      <div className="border-t border-mediumgray">
        <dl className="divide-y divide-mediumgray">
          {/* Template Name */}
          <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
            <dt className="text-sm font-medium">Template Name</dt>
            <dd className="mt-1 text-sm text-darkergray sm:mt-0 sm:col-span-2 font-semibold">
              {template.name}
            </dd>
          </div>

          {/* Description */}
          <div className="bg-lightergray px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
            <dt className="text-sm font-medium">Description</dt>
            <dd className="mt-1 text-sm text-darkergray sm:mt-0 sm:col-span-2">
              <pre className="whitespace-pre-wrap font-sans">
                {template.description}
              </pre>
            </dd>
          </div>

          {/* SMS Metrics */}
          <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
            <dt className="text-sm font-medium">SMS Metrics</dt>
            <dd className="mt-1 text-sm text-darkergray sm:mt-0 sm:col-span-2">
              <SmsMetricsDisplay 
                description={template.description}
                placeholders={template.placeholders}
              />
            </dd>
          </div>

          {/* Placeholders */}
          <div className="bg-lightergray px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
            <dt className="text-sm font-medium">Placeholders</dt>
            <dd className="mt-1 text-sm text-darkergray sm:mt-0 sm:col-span-2">
              {template.placeholders && template.placeholders.length > 0 ? (
                <div className="space-y-2">
                  <div className="flex flex-wrap gap-2">
                    {template.placeholders.map((placeholder, index) => (
                      <span
                        key={index}
                        className="inline-flex items-center gap-1 border border-mediumgray text-darkergray px-3 py-1 rounded text-sm font-mono"
                      >
                        <PiBracketsCurly size={14} />
                        {`{{${placeholder}}}`}
                      </span>
                    ))}
                  </div>
                </div>
              ) : (
                <span className="text-darkgray italic">
                  No placeholders defined
                </span>
              )}
            </dd>
          </div>

          {/* Timestamps */}
          <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
            <dt className="text-sm font-medium">Created At</dt>
            <dd className="mt-1 text-sm text-darkergray sm:mt-0 sm:col-span-2 flex items-center gap-2">
              <FaClock className="text-darkgray" size={12} />
              {template.createdAt
                ? formatDate(template.createdAt)
                : "Not available"}
            </dd>
          </div>

          <div className="bg-lightergray px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
            <dt className="text-sm font-medium">Last Updated</dt>
            <dd className="mt-1 text-sm text-darkergray sm:mt-0 sm:col-span-2 flex items-center gap-2">
              <FaClock className="text-darkgray" size={12} />
              {template.updatedAt
                ? formatDate(template.updatedAt)
                : "Not available"}
            </dd>
          </div>
        </dl>
      </div>
    </div>
  );
}